<template>
    <div>
        <draggable
            class="overflow-auto space-y-2"
            chosenClass="bg-gray-100"
            handle=".handle"
            :list="resourcesArray"
            :group="name"
            @start="drag=true"
            direction="vertical"
            @end="drag=false"
            item-key="unique_id"
        >
            <template #item="{element}">
                <ResourceWrapper
                    :data="element"
                    :unique_id="element.unique_id"
                    @remove="removeResource"
                    @toggle="toggleResource(element)"
                ></ResourceWrapper>
            </template>
        </draggable>

        <div v-if="!resourcesArray.length">
            <div class="flex flex-col justify-center items-center gap-4 text-center p-10 border rounded">
                <div class="space-y-2">
                    <h2 class="font-semibold text-lg">{{ zeroFieldsTitle }}</h2>
                    <p class="text-gray-500 text-sm">{{ zeroFieldsMessage }}</p>
                </div>
            </div>
        </div>

        <div class="w-max mt-2">
            <options :auto-position="true" :is-right="false" width="!max-w-none !whitespace-normal w-[210px] sm:w-[380px] max-w-[calc(100vw-2rem)]" options-container-class="optionsContainerClass">
                <template #toggle>
                    <btn type="secondary" icon-name="more" icon-right="true" v-on:click.prevent>Add resource</btn>
                </template>
                <template v-slot:items="{ setShow }">
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-0.5 p-1.5">
                        <button
                            v-for="resource in resourcesList"
                            :key="resource.type"
                            type="button"
                            class="flex items-start gap-2.5 px-3 py-2 text-left rounded hover:bg-gray-100 focus:outline-none dark:hover:bg-gray-800"
                            @click="addResource(resource.type);setShow(false)"
                        >
                            <svg class="w-5 h-5 shrink-0 mt-0.5 text-gray-500 hidden" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" v-html="getResourceIcon(resource.type)"></svg>
                            <span>
                                <span class="block text-sm font-medium text-gray-900 dark:text-gray-300">{{ resource.name }}</span>
                                <span v-if="resource.description" class="block text-xs text-gray-400 leading-tight">{{ resource.description }}</span>
                            </span>
                        </button>
                    </div>
                </template>
            </options>
        </div>

        <input type="hidden" :name="name" :value="resourcesString">
    </div>
</template>

<script setup>
import {ref, defineProps, provide, computed} from 'vue';
import {uniqueId, resourceOptions, getResourceIcon} from "./helpers";
import draggable from 'vuedraggable';
import ResourceWrapper from './resources/ResourceWrapper.vue';

const props = defineProps({
    name: String,
    resources: Array,
    programType: null,
    fileUploadUrl: String,
    fileDeleteUrl: String,
    zeroFieldsTitle: String,
    zeroFieldsMessage: String,
    optionsContainerClass: {
        type: String,
        default: 'h-full'
    }
});

provide('fileUrls', {
    upload: props.fileUploadUrl,
    delete: props.fileDeleteUrl,
});

const drag = ref(false);
const resourcesArray = ref(props.resources);
const resourcesString = computed(() => JSON.stringify(resourcesArray.value));
const resourcesList = resourceOptions(props.programType);

// Ensure only one resource is open at initialization
const openResources = resourcesArray.value.filter(r => r.edit);
if (openResources.length > 1) {
    // Close all except the first open one
    let foundFirst = false;
    resourcesArray.value.forEach(resource => {
        if (resource.edit) {
            if (foundFirst) {
                resource.edit = false;
            } else {
                foundFirst = true;
            }
        }
    });
}

const zeroFieldsTitle = ref(props.zeroFieldsTitle);
const zeroFieldsMessage = ref(props.zeroFieldsMessage);

const addResource = (type) => {
    // Close all existing resources
    resourcesArray.value.forEach(resource => {
        resource.edit = false;
    });

    // Add new resource (opened by default)
    resourcesArray.value.push({
        type: type,
        unique_id: uniqueId(),
        edit: true,
        title: type === 'how_it_works' ? 'How it works' : '',
        description: '',
        main_image: {},
        main_image_retina_resize: false,
    });
}

const toggleResource = (element) => {
    const isOpening = !element.edit;

    // If opening, close all other resources first
    if (isOpening) {
        resourcesArray.value.forEach(resource => {
            if (resource.unique_id !== element.unique_id) {
                resource.edit = false;
            }
        });
    }

    // Toggle the clicked resource
    element.edit = !element.edit;
}

const removeResource = (uniqueId) => {
    let resourceIndex = resourcesArray.value.findIndex(resource => resource.unique_id === uniqueId);
    if (resourceIndex !== -1) {
        resourcesArray.value.splice(resourceIndex, 1);
    }
}
</script>
